import React, { useState, useMemo } from 'react';
import { ShoppingBag, Clock, Package, Check, X, Truck, User, Bike, MapPin, Filter } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { 
  OFFICES, PRODUCTS, ORDERS, BOOKINGS, MODULES, FULFILLMENT_TYPES,
  getAllOrdersForOffice,
} from '../data/database';
import { useI18n } from '../i18n';

const getStatusColor = (status) => {
  switch (status) {
    case 'confirmed': return 'bg-emerald-100 text-emerald-700';
    case 'pending': return 'bg-amber-100 text-amber-700';
    case 'completed': return 'bg-gray-100 text-gray-700';
    case 'delivered': return 'bg-emerald-100 text-emerald-700';
    case 'shipped': return 'bg-blue-100 text-blue-700';
    case 'preparing': return 'bg-purple-100 text-purple-700';
    case 'ready': return 'bg-indigo-100 text-indigo-700';
    case 'cancelled': return 'bg-red-100 text-red-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const getStatusText = (status, t) => {
  const statusMap = {
    pending: 'In attesa',
    confirmed: 'Confermato',
    preparing: 'In preparazione',
    ready: 'Pronto',
    shipped: 'Spedito',
    delivered: 'Consegnato',
    completed: 'Completato',
    cancelled: 'Annullato'
  };
  return statusMap[status] || status;
};

const getFulfillmentIcon = (type) => {
  switch (type) {
    case 'delivery': return Bike;
    case 'shipping': return Truck;
    case 'takeaway':
    case 'pickup': return ShoppingBag;
    default: return Package;
  }
};

const getFulfillmentLabel = (type) => {
  const labels = {
    delivery: 'Delivery',
    shipping: 'Spedizione',
    takeaway: 'Asporto',
    pickup: 'Ritiro',
  };
  return labels[type] || type;
};

const OrdersPage = ({ onNavigate }) => {
  const { cart, cartTotal, cartCount, updateCartQty, clearCart, addToCart, userMode, selectedOffice } = useApp();
  const { t } = useI18n();
  const isPartner = userMode === 'partner';
  const [activeTab, setActiveTab] = useState(isPartner ? 'received' : (cartCount > 0 ? 'cart' : 'orders'));
  
  // Dynamic office data from context
  const officeId = selectedOffice?.id || 1;
  const officeType = selectedOffice?.type || 'salon';
  const typeConfig = getOfficeTypeConfig(officeType);
  
  // Get orders dynamically for current office
  const [receivedOrders, setReceivedOrders] = useState(() => {
    if (!isPartner) return [];
    return getAllOrdersForOffice(officeId);
  });
  
  // Filter state for fulfillment type
  const [selectedFulfillment, setSelectedFulfillment] = useState(null);
  
  // Get unique fulfillment types from orders
  const availableFulfillments = useMemo(() => {
    const types = [...new Set(receivedOrders.map(o => o.fulfillmentType))];
    return types.filter(Boolean);
  }, [receivedOrders]);
  
  // Filtered orders
  const filteredOrders = useMemo(() => {
    if (!selectedFulfillment) return receivedOrders;
    return receivedOrders.filter(o => o.fulfillmentType === selectedFulfillment);
  }, [receivedOrders, selectedFulfillment]);
  
  const updateOrderStatus = (orderId, newStatus) => {
    setReceivedOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
  };

  // Status translation helper
  const getStatusTextLocal = (status) => getStatusText(status, t);

  // Enrich orders with business/product info (for consumer)
  const enrichedOrders = ORDERS.map(order => ({
    ...order,
    business: OFFICES.find(b => b.id === order.officeId),
    products: order.items.map(item => ({
      ...item,
      product: PRODUCTS.find(p => p.id === item.productId)
    }))
  }));

  // Tabs config based on mode
  const consumerTabs = [
    { id: 'cart', label: t('orders', 'cart'), count: cartCount },
    { id: 'orders', label: t('orders', 'orders'), count: enrichedOrders.length }
  ];
  
  const partnerTabs = [
    { id: 'received', label: 'Ricevuti', count: receivedOrders.filter(o => o.status !== 'delivered' && o.status !== 'cancelled').length },
    { id: 'history', label: 'Storico', count: receivedOrders.filter(o => o.status === 'delivered' || o.status === 'cancelled').length }
  ];
  
  const tabs = isPartner ? partnerTabs : consumerTabs;

  return (
    <div className="min-h-screen bg-gray-50 pb-20 lg:pb-8">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-500 to-teal-600 pt-6 pb-6">
        <div className="max-w-screen-2xl mx-auto px-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-shrink-0">
              <h1 className="text-2xl lg:text-3xl font-bold text-white whitespace-nowrap">
                {isPartner ? 'Ordini' : t('orders', 'myActivities')}
              </h1>
              <p className="text-emerald-100 text-sm lg:text-base whitespace-nowrap">
                {isPartner ? 'Gestisci gli ordini' : t('orders', 'manageOrdersAndCart')}
              </p>
            </div>
            
            {/* Tabs */}
            <div className="flex bg-white/20 backdrop-blur rounded-xl p-1 flex-shrink-0">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeTab === tab.id ? 'bg-white text-emerald-600' : 'text-white'
                  }`}
                >
                  <span>{tab.label}</span>
                  {tab.count > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-emerald-100 text-emerald-600' : 'bg-white/20 text-white'}`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-6 py-6">
        {/* Cart */}
        {activeTab === 'cart' && (
          <div>
            {cart.length > 0 ? (
              <>
                <div className="space-y-3 mb-4">
                  {cart.map((item) => {
                    const business = OFFICES.find(b => b.id === item.officeId);
                    return (
                      <div key={`${item.type}-${item.id}`} className="bg-white rounded-xl p-4 shadow-sm">
                        <div className="flex gap-4">
                          <img src={item.image} alt={item.name} className="w-20 h-20 rounded-xl object-cover" />
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{item.name}</h4>
                            <p className="text-sm text-gray-500">{business?.name}</p>
                            <p className="font-bold text-emerald-600 mt-1">€{item.price}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button 
                              onClick={() => updateCartQty(item.id, item.type, item.qty - 1)}
                              className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200"
                            >
                              -
                            </button>
                            <span className="w-8 text-center font-medium">{item.qty}</span>
                            <button 
                              onClick={() => updateCartQty(item.id, item.type, item.qty + 1)}
                              className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-gray-500">{t('orders', 'total')}</span>
                    <span className="text-2xl font-bold text-gray-900">€{cartTotal.toFixed(2)}</span>
                  </div>
                  <button className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors">
                    {t('orders', 'proceedToPayment')}
                  </button>
                  <button 
                    onClick={clearCart}
                    className="w-full text-gray-500 py-2 text-sm mt-2 hover:text-red-500"
                  >
                    {t('orders', 'clearCart')}
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('orders', 'emptyCart')}</h3>
                <p className="text-gray-500 mb-4">{t('orders', 'addProductsToCart')}</p>
                <button 
                  onClick={() => onNavigate('search', { type: 'products' })}
                  className="bg-emerald-500 text-white px-6 py-3 rounded-xl font-medium hover:bg-emerald-600 transition-colors"
                >
                  {t('orders', 'exploreProducts')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Orders */}
        {activeTab === 'orders' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {enrichedOrders.map((order) => (
              <div 
                key={order.id} 
                className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => onNavigate('order-detail', { id: order.id })}
              >
                <div className="flex gap-4">
                  <img src={order.business?.image} alt={order.business?.name} className="w-20 h-20 lg:w-24 lg:h-24 rounded-xl object-cover" />
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900 lg:text-lg">{order.business?.name}</h4>
                        <p className="text-sm text-gray-500 line-clamp-1">
                          {order.products.map(p => p.product?.name).join(', ')}
                        </p>
                      </div>
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${getStatusColor(order.status)}`}>
                        {getStatusTextLocal(order.status)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-sm text-gray-400">{order.date}</p>
                      <p className="font-bold text-gray-900 lg:text-lg">€{order.total.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <button 
                    onClick={(e) => { e.stopPropagation(); onNavigate('order-detail', { id: order.id }); }}
                    className="flex-1 flex items-center justify-center gap-1 bg-gray-100 text-gray-700 py-2.5 lg:py-3 rounded-xl text-sm font-medium hover:bg-gray-200"
                  >
                    <Package className="w-4 h-4" />
                    {t('orders', 'trackOrder')}
                  </button>
                  <button 
                    onClick={(e) => { 
                      e.stopPropagation();
                      // Riordina - aggiunge tutti i prodotti al carrello
                      order.products.forEach(p => {
                        if (p.product) {
                          for (let i = 0; i < (p.qty || 1); i++) {
                            addToCart(p.product, 'product');
                          }
                        }
                      });
                      onNavigate('cart');
                    }}
                    className="flex-1 bg-emerald-500 text-white py-2.5 lg:py-3 rounded-xl text-sm font-medium hover:bg-emerald-600"
                  >
                    {t('orders', 'reorder')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Partner: Received Orders */}
        {activeTab === 'received' && isPartner && (
          <div className="space-y-4">
            {/* Fulfillment Type Filter */}
            {availableFulfillments.length > 1 && (
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Filter className="w-5 h-5 text-emerald-600" />
                  <h3 className="font-semibold text-gray-900">Tipo consegna</h3>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => setSelectedFulfillment(null)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      !selectedFulfillment ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Tutti ({receivedOrders.filter(o => o.status !== 'delivered' && o.status !== 'cancelled').length})
                  </button>
                  {availableFulfillments.map((type) => {
                    const Icon = getFulfillmentIcon(type);
                    const count = receivedOrders.filter(o => o.fulfillmentType === type && o.status !== 'delivered' && o.status !== 'cancelled').length;
                    return (
                      <button
                        key={type}
                        onClick={() => setSelectedFulfillment(type)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          selectedFulfillment === type ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {getFulfillmentLabel(type)} ({count})
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {filteredOrders.filter(o => o.status !== 'delivered' && o.status !== 'cancelled').length > 0 ? (
              filteredOrders.filter(o => o.status !== 'delivered' && o.status !== 'cancelled').map((order) => {
                const FulfillmentIcon = getFulfillmentIcon(order.fulfillmentType);
                return (
                <div 
                  key={order.id} 
                  className="bg-white rounded-xl p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => onNavigate && onNavigate('order-detail', { id: order.id })}
                  data-testid={`order-${order.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold text-white flex-shrink-0 ${
                      order.isElit ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-emerald-500'
                    }`}>
                      {order.clientAvatar || order.clientName?.substring(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold text-gray-900">{order.clientName}</h4>
                          {order.isElit && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">ELIT</span>}
                        </div>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${getStatusColor(order.status)}`}>
                          {getStatusTextLocal(order.status)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {order.items.map(i => `${i.name} x${i.qty}`).join(', ')}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 flex-wrap">
                        <span>{order.date}</span>
                        <span className="flex items-center gap-1">
                          <FulfillmentIcon className="w-4 h-4" />
                          {getFulfillmentLabel(order.fulfillmentType)}
                        </span>
                        {order.estimatedDelivery && (
                          <span className="text-emerald-600">Consegna: {order.estimatedDelivery}</span>
                        )}
                        {order.pickupTime && (
                          <span className="text-emerald-600">Ritiro: {order.pickupTime}</span>
                        )}
                      </div>
                      {(order.fulfillmentType === 'delivery' || order.fulfillmentType === 'shipping') && order.shipping?.address && (
                        <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {order.shipping.address}
                        </p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-lg text-gray-900">€{order.total.toFixed(2)}</p>
                    </div>
                  </div>
                  
                  {/* Actions based on status */}
                  <div className="flex gap-2 mt-4" onClick={(e) => e.stopPropagation()}>
                    {order.status === 'pending' && (
                      <>
                        <button 
                          onClick={() => updateOrderStatus(order.id, 'cancelled')}
                          className="flex-1 flex items-center justify-center gap-1 bg-red-50 text-red-600 py-2.5 rounded-xl text-sm font-medium hover:bg-red-100"
                        >
                          <X className="w-4 h-4" /> Rifiuta
                        </button>
                        <button 
                          onClick={() => updateOrderStatus(order.id, 'confirmed')}
                          className="flex-1 flex items-center justify-center gap-1 bg-emerald-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-emerald-600"
                        >
                          <Check className="w-4 h-4" /> Conferma
                        </button>
                      </>
                    )}
                    {order.status === 'confirmed' && (
                      <button 
                        onClick={() => updateOrderStatus(order.id, order.fulfillmentType === 'delivery' ? 'preparing' : 'shipped')}
                        className="flex-1 flex items-center justify-center gap-1 bg-purple-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-purple-600"
                      >
                        <Package className="w-4 h-4" /> In preparazione
                      </button>
                    )}
                    {order.status === 'preparing' && (
                      <button 
                        onClick={() => updateOrderStatus(order.id, 'ready')}
                        className="flex-1 flex items-center justify-center gap-1 bg-indigo-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-indigo-600"
                      >
                        <Check className="w-4 h-4" /> Pronto
                      </button>
                    )}
                    {order.status === 'ready' && (
                      <button 
                        onClick={() => updateOrderStatus(order.id, 'delivered')}
                        className="flex-1 flex items-center justify-center gap-1 bg-emerald-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-emerald-600"
                      >
                        {order.fulfillmentType === 'takeaway' || order.fulfillmentType === 'pickup' ? (
                          <><ShoppingBag className="w-4 h-4" /> Ritirato</>
                        ) : (
                          <><Truck className="w-4 h-4" /> Consegnato</>
                        )}
                      </button>
                    )}
                    {order.status === 'shipped' && (
                      <button 
                        onClick={() => updateOrderStatus(order.id, 'delivered')}
                        className="flex-1 flex items-center justify-center gap-1 bg-emerald-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-emerald-600"
                      >
                        <Check className="w-4 h-4" /> Segna consegnato
                      </button>
                    )}
                  </div>
                </div>
              );
              })
            ) : (
              <div className="text-center py-12">
                <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun ordine da gestire</h3>
                <p className="text-gray-500">
                  {selectedFulfillment 
                    ? `Nessun ordine ${getFulfillmentLabel(selectedFulfillment).toLowerCase()} in attesa`
                    : 'Non hai ordini in attesa o in lavorazione'
                  }
                </p>
              </div>
            )}
          </div>
        )}

        {/* Partner: Order History */}
        {activeTab === 'history' && isPartner && (
          <div className="space-y-4">
            {filteredOrders.filter(o => o.status === 'delivered' || o.status === 'cancelled').length > 0 ? (
              filteredOrders.filter(o => o.status === 'delivered' || o.status === 'cancelled').map((order) => (
                <div 
                  key={order.id} 
                  className="bg-white rounded-xl p-4 shadow-sm opacity-75 cursor-pointer hover:opacity-100 transition-opacity"
                  onClick={() => onNavigate && onNavigate('order-detail', { id: order.id })}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center font-semibold text-white flex-shrink-0 bg-gray-400">
                      {order.clientAvatar || order.clientName?.substring(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="font-semibold text-gray-900">{order.clientName}</h4>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${getStatusColor(order.status)}`}>
                          {getStatusTextLocal(order.status)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {order.items.map(i => `${i.name} x${i.qty}`).join(', ')}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">{order.date}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-gray-900">€{order.total.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Nessuno storico</h3>
                <p className="text-gray-500">Non hai ancora ordini completati o annullati</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
